import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Package, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n";
import { getArtworkBySlug, getArtworks, type Artwork } from "@/lib/api";
import { getFallbackArtworkBySlug } from "@/lib/fallbackArtworks";
import { formatPrice, getArtworkDescription, getArtworkTitle } from "@/lib/utils";
import ArtworkCard from "@/components/ArtworkCard";
import { useCart } from "@/lib/cart";

export default function ArtworkDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [relatedArtworks, setRelatedArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getArtworkBySlug(slug)
      .then((data) => {
        setArtwork(data);
        return getArtworks({ category: data.category, limit: 4 });
      })
      .then((res) => {
        setRelatedArtworks(res.artworks.filter((item) => item.slug !== slug).slice(0, 3));
      })
      .catch((error) => {
        console.error(error);
        const fallbackArtwork = getFallbackArtworkBySlug(slug);
        if (fallbackArtwork) {
          setArtwork(fallbackArtwork);
          setRelatedArtworks([]);
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="page-shell">
        <div className="container">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="panel aspect-[3/4] animate-pulse bg-[var(--color-sand)]" />
            <div className="panel animate-pulse p-8">
              <div className="mb-4 h-8 w-1/3 rounded-full bg-[var(--color-sand)]" />
              <div className="mb-4 h-16 w-4/5 rounded-[1rem] bg-[var(--color-sand)]" />
              <div className="h-32 rounded-[1rem] bg-[var(--color-sand)]" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="page-shell">
        <div className="container">
          <div className="panel p-12 text-center">
            <h1 className="mb-3 text-[2.4rem]">Artwork not found</h1>
            <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>
              {t.common.noResults}
            </p>
            <Link to="/gallery" className="btn-secondary">
              {t.nav.gallery}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const title = getArtworkTitle(artwork, locale);
  const description = getArtworkDescription(artwork, locale);
  const isAvailable = artwork.status === "available";
  const additionalImages: string[] = artwork.additional_images
    ? JSON.parse(artwork.additional_images)
    : [];
  const allImages = [artwork.image_url, ...additionalImages].filter(Boolean) as string[];

  return (
    <div className="page-shell">
      <div className="container">
        <button onClick={() => navigate(-1)} className="btn-ghost mb-6 px-0">
          <ArrowLeft size={16} />
          {t.nav.gallery}
        </button>

        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-4">
            <div className="panel overflow-hidden">
              <img
                src={allImages[selectedImage] || "/placeholder-artwork.jpg"}
                alt={title}
                className="w-full aspect-[3/4] object-cover"
              />
            </div>
            {allImages.length > 1 && (
              <div className="flex flex-wrap gap-3">
                {allImages.map((image, index) => (
                  <button
                    key={image}
                    onClick={() => setSelectedImage(index)}
                    className="overflow-hidden rounded-2xl border"
                    style={{
                      borderColor:
                        index === selectedImage ? "var(--color-terracotta)" : "var(--color-border)",
                      opacity: index === selectedImage ? 1 : 0.72,
                    }}
                  >
                    <img src={image} alt="" className="h-20 w-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <section className="panel p-7 md:p-8">
              <span className="eyebrow">
                {t.categories[artwork.category as keyof typeof t.categories]}
              </span>
              <h1 className="mb-8 text-[2.7rem] leading-[1.12] md:text-[3.4rem]">{title}</h1>
              <p
                className="mb-6 text-[1.7rem]"
                style={{ color: isAvailable ? "var(--color-terracotta)" : "var(--color-text-light)" }}
              >
                {isAvailable ? `Available · ${formatPrice(artwork.price, artwork.currency)}` : t.artwork.sold}
              </p>

              {description && (
                <p className="mb-7" style={{ color: "var(--color-text-muted)" }}>
                  {description}
                </p>
              )}

              <div className="detail-list mb-7">
                {artwork.technique && (
                  <div className="detail-row text-sm">
                    <span style={{ color: "var(--color-text-light)" }}>{t.artwork.technique}</span>
                    <strong>{artwork.technique}</strong>
                  </div>
                )}
                {artwork.dimensions && (
                  <div className="detail-row text-sm">
                    <span style={{ color: "var(--color-text-light)" }}>{t.artwork.dimensions}</span>
                    <strong>{artwork.dimensions}</strong>
                  </div>
                )}
                {artwork.year && (
                  <div className="detail-row text-sm">
                    <span style={{ color: "var(--color-text-light)" }}>{t.artwork.year}</span>
                    <strong>{artwork.year}</strong>
                  </div>
                )}
                <div className="detail-row text-sm">
                  <span style={{ color: "var(--color-text-light)" }}>{t.artwork.category}</span>
                  <strong>{t.categories[artwork.category as keyof typeof t.categories]}</strong>
                </div>
              </div>

              {isAvailable ? (
                <button onClick={() => addItem({
                  id: artwork.id,
                  slug: artwork.slug,
                  title,
                  category: artwork.category,
                  image: artwork.image_url || "/placeholder-artwork.jpg",
                  price: artwork.price,
                  currency: artwork.currency,
                  stockQuantity: artwork.stock_quantity,
                })} className="btn-primary w-full">
                  {t.artwork.buyNow}
                  <ArrowRight size={16} />
                </button>
              ) : (
                <Link to="/contact" className="btn-secondary w-full">
                  {t.artwork.inquire}
                </Link>
              )}
            </section>

            <section className="panel p-6">
              <div className="flex items-start gap-3">
                <Package size={18} style={{ color: "var(--color-terracotta)", marginTop: 3 }} />
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                  {t.artwork.shippingInfo}
                </p>
              </div>
              <div className="mt-4 flex items-start gap-3">
                <ShieldCheck size={18} style={{ color: "var(--color-terracotta)", marginTop: 3 }} />
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                  Certificate of authenticity included with each purchase.
                </p>
              </div>
            </section>
          </div>
        </div>

        {relatedArtworks.length > 0 && (
          <section className="section-padding pb-0">
            <div className="section-heading">
              <div>
                <span className="eyebrow">{t.artwork.relatedWorks}</span>
                <h2>{t.artwork.relatedWorks}</h2>
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {relatedArtworks.map((item) => (
                <ArtworkCard key={item.id} artwork={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
